import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    deleteEvalRun,
    listEvalRuns,
    loadEvalRun,
    renameEvalRun,
    runTabexawyEval,
} from './runTabexawyEval';
import './TabexawyEvalPanel.css';

function formatOverall(summary = {}) {
    return {
        passed: summary.passed ?? 0,
        failed: summary.failed ?? 0,
        total: summary.total ?? 0,
        tokens: summary.total_tokens ?? 0,
        cost: summary.cost_usd_display || `$${Number(summary.cost_usd || 0).toFixed(6)}`,
        seconds: summary.elapsed_seconds ?? 0,
    };
}

function EvalCaseRow({ row, isRtl }) {
    const [openQa, setOpenQa] = useState(false);
    const tokens = row.total_tokens ?? row.usage?.total_tokens ?? 0;
    const cost = row.cost_usd_display || row.usage?.cost_usd_display || '$0.000000';
    const seconds = row.elapsed_seconds ?? ((Number(row.ms) || 0) / 1000).toFixed(1);

    return (
        <div className={`tbx-eval-row ${row.passed ? 'is-pass' : 'is-fail'}`}>
            <div className="tbx-eval-row-top">
                <span className="tbx-eval-badge">{row.passed ? 'PASS' : 'FAIL'}</span>
                <span className="tbx-eval-row-title">{row.title || row.id}</span>
                <span className="tbx-eval-ms">{seconds}s</span>
            </div>
            <div className="tbx-eval-metrics">
                <span>
                    {tokens} {isRtl ? 'توكن' : 'tok'}
                </span>
                <span>·</span>
                <span>{cost}</span>
                <span>·</span>
                <span>
                    {seconds} {isRtl ? 'ث' : 's'}
                </span>
                {row.fastPath ? (
                    <>
                        <span>·</span>
                        <span className="tbx-eval-fast">{isRtl ? 'بدون AI' : 'no AI'}</span>
                    </>
                ) : null}
            </div>
            {row.fail && <div className="tbx-eval-reason">{row.fail}</div>}
            {row.tools?.length > 0 && <div className="tbx-eval-tools">{row.tools.join(', ')}</div>}

            <button
                type="button"
                className="tbx-eval-qa-toggle"
                onClick={() => setOpenQa((v) => !v)}
                aria-expanded={openQa}
            >
                {openQa
                    ? isRtl
                        ? 'إخفاء السؤال والإجابة'
                        : 'Hide question & answer'
                    : isRtl
                      ? 'عرض السؤال والإجابة'
                      : 'Show question & answer'}
            </button>
            {openQa && (
                <div className="tbx-eval-qa">
                    <div className="tbx-eval-qa-block">
                        <div className="tbx-eval-qa-label">{isRtl ? 'السؤال' : 'Question'}</div>
                        <div className="tbx-eval-qa-text">{row.prompt || '—'}</div>
                    </div>
                    <div className="tbx-eval-qa-block">
                        <div className="tbx-eval-qa-label">{isRtl ? 'الإجابة' : 'Answer'}</div>
                        <div className="tbx-eval-qa-text">{row.answer || row.answerPreview || '—'}</div>
                    </div>
                </div>
            )}
        </div>
    );
}

function OverallCards({ summary, isRtl }) {
    const o = formatOverall(summary || {});
    return (
        <div className="tbx-eval-overall">
            <div>
                <span className="tbx-eval-overall-label">{isRtl ? 'النتيجة' : 'Result'}</span>
                <strong>
                    {o.passed}/{o.total}
                    {o.failed ? ` · ${o.failed} fail` : ''}
                </strong>
            </div>
            <div>
                <span className="tbx-eval-overall-label">{isRtl ? 'التوكنات' : 'Tokens'}</span>
                <strong>{Number(o.tokens).toLocaleString()}</strong>
            </div>
            <div>
                <span className="tbx-eval-overall-label">{isRtl ? 'التكلفة' : 'Cost'}</span>
                <strong>{o.cost}</strong>
            </div>
            <div>
                <span className="tbx-eval-overall-label">{isRtl ? 'الوقت' : 'Time'}</span>
                <strong>
                    {o.seconds}
                    {isRtl ? ' ث' : 's'}
                </strong>
            </div>
        </div>
    );
}

function CompareView({ left, right, isRtl }) {
    const leftMap = useMemo(() => {
        const m = new Map();
        (left?.cases || []).forEach((c) => m.set(c.id, c));
        return m;
    }, [left]);
    const rightMap = useMemo(() => {
        const m = new Map();
        (right?.cases || []).forEach((c) => m.set(c.id, c));
        return m;
    }, [right]);
    const ids = useMemo(() => {
        const set = new Set([...(leftMap.keys()), ...(rightMap.keys())]);
        return [...set];
    }, [leftMap, rightMap]);

    const l = formatOverall(left?.summary);
    const r = formatOverall(right?.summary);

    return (
        <div className="tbx-eval-compare">
            <div className="tbx-eval-compare-heads">
                <div>
                    <div className="tbx-eval-compare-name">{left?.name || 'A'}</div>
                    <OverallCards summary={left?.summary} isRtl={isRtl} />
                </div>
                <div>
                    <div className="tbx-eval-compare-name">{right?.name || 'B'}</div>
                    <OverallCards summary={right?.summary} isRtl={isRtl} />
                </div>
            </div>
            <div className="tbx-eval-compare-delta">
                <span>
                    {isRtl ? 'فرق التوكنات' : 'Δ tokens'}: {(r.tokens - l.tokens).toLocaleString()}
                </span>
                <span>
                    {isRtl ? 'فرق التكلفة' : 'Δ cost'}: $
                    {(Number(right?.summary?.cost_usd || 0) - Number(left?.summary?.cost_usd || 0)).toFixed(6)}
                </span>
                <span>
                    {isRtl ? 'فرق الوقت' : 'Δ time'}:{' '}
                    {(Number(r.seconds) - Number(l.seconds)).toFixed(1)}
                    {isRtl ? ' ث' : 's'}
                </span>
            </div>
            <div className="tbx-eval-compare-rows">
                {ids.map((id) => {
                    const a = leftMap.get(id);
                    const b = rightMap.get(id);
                    const samePass = Boolean(a?.passed) === Boolean(b?.passed);
                    return (
                        <div key={id} className={`tbx-eval-compare-row ${samePass ? '' : 'is-diff'}`}>
                            <div className="tbx-eval-compare-case">{a?.title || b?.title || id}</div>
                            <div className={a?.passed ? 'is-pass' : 'is-fail'}>
                                {a ? (a.passed ? 'PASS' : 'FAIL') : '—'} · {a?.total_tokens ?? 0} tok ·{' '}
                                {a?.elapsed_seconds ?? '—'}s
                            </div>
                            <div className={b?.passed ? 'is-pass' : 'is-fail'}>
                                {b ? (b.passed ? 'PASS' : 'FAIL') : '—'} · {b?.total_tokens ?? 0} tok ·{' '}
                                {b?.elapsed_seconds ?? '—'}s
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

export default function TabexawyEvalPanel({ open, onClose, instituteId, isRtl }) {
    const [view, setView] = useState('history'); // history | draft | detail | compare
    const [suite, setSuite] = useState('ask');
    const [allowWrites, setAllowWrites] = useState(false);
    const [runName, setRunName] = useState('');
    const [running, setRunning] = useState(false);
    const [progress, setProgress] = useState(null);
    const [results, setResults] = useState([]);
    const [summary, setSummary] = useState(null);
    const [activeRun, setActiveRun] = useState(null);
    const [runs, setRuns] = useState([]);
    const [compareIds, setCompareIds] = useState([]);
    const [compareRuns, setCompareRuns] = useState({ left: null, right: null });
    const [error, setError] = useState(null);
    const abortRef = useRef(null);

    const refreshRuns = useCallback(async () => {
        if (!instituteId) return;
        const data = await listEvalRuns({ instituteId });
        setRuns(Array.isArray(data.runs) ? data.runs : []);
    }, [instituteId]);

    useEffect(() => {
        if (!open || !instituteId) return;
        let cancelled = false;
        listEvalRuns({ instituteId })
            .then((data) => {
                if (cancelled) return;
                setRuns(Array.isArray(data.runs) ? data.runs : []);
                setView('history');
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [open, instituteId]);

    useEffect(() => {
        return () => {
            abortRef.current?.abort();
        };
    }, []);

    const stop = useCallback(() => {
        abortRef.current?.abort();
        abortRef.current = null;
        setRunning(false);
    }, []);

    const startNewTest = useCallback(() => {
        setError(null);
        setResults([]);
        setSummary(null);
        setActiveRun(null);
        setProgress(null);
        setRunName('');
        setView('draft');
    }, []);

    const openRun = useCallback(
        async (runId) => {
            setError(null);
            try {
                const run = await loadEvalRun({ instituteId, runId });
                setActiveRun(run);
                setResults(run.cases || []);
                setSummary(run.summary || null);
                setView('detail');
            } catch (err) {
                setError(err.message || String(err));
            }
        },
        [instituteId],
    );

    const toggleCompare = useCallback((runId) => {
        setCompareIds((prev) => {
            if (prev.includes(runId)) return prev.filter((id) => id !== runId);
            if (prev.length >= 2) return [prev[1], runId];
            return [...prev, runId];
        });
    }, []);

    const openCompare = useCallback(async () => {
        if (compareIds.length !== 2) return;
        setError(null);
        try {
            const [a, b] = await Promise.all([
                loadEvalRun({ instituteId, runId: compareIds[0] }),
                loadEvalRun({ instituteId, runId: compareIds[1] }),
            ]);
            // Older first for delta readability
            const left = new Date(a.saved_at) <= new Date(b.saved_at) ? a : b;
            const right = left === a ? b : a;
            setCompareRuns({ left, right });
            setView('compare');
        } catch (err) {
            setError(err.message || String(err));
        }
    }, [compareIds, instituteId]);

    const removeRun = useCallback(
        async (runId) => {
            try {
                const data = await deleteEvalRun({ instituteId, runId });
                setRuns(Array.isArray(data.runs) ? data.runs : []);
                setCompareIds((prev) => prev.filter((id) => id !== runId));
                if (activeRun?.id === runId) {
                    setActiveRun(null);
                    setView('history');
                }
            } catch (err) {
                setError(err.message || String(err));
            }
        },
        [instituteId, activeRun],
    );

    const renameRun = useCallback(
        async (runId, currentName) => {
            const next = window.prompt(isRtl ? 'اسم المجموعة' : 'Collection name', currentName || '');
            if (next == null) return;
            const name = String(next).trim();
            if (!name) return;
            try {
                await renameEvalRun({ instituteId, runId, name });
                await refreshRuns();
                if (activeRun?.id === runId) {
                    setActiveRun((prev) => (prev ? { ...prev, name } : prev));
                }
            } catch (err) {
                setError(err.message || String(err));
            }
        },
        [instituteId, isRtl, refreshRuns, activeRun],
    );

    const run = useCallback(async () => {
        if (!instituteId || running) return;
        setError(null);
        setRunning(true);
        setResults([]);
        setSummary(null);
        setActiveRun(null);
        setView('draft');
        setProgress({ index: 0, total: 0, status: 'starting' });
        const controller = new AbortController();
        abortRef.current = controller;

        try {
            const outcome = await runTabexawyEval({
                instituteId,
                suite,
                allowWrites: suite === 'ask' ? false : allowWrites,
                runName: runName.trim() || undefined,
                signal: controller.signal,
                onProgress: (p) => {
                    setProgress(p);
                    if (p.row) {
                        setResults((prev) => {
                            const next = prev.filter((r) => r.id !== p.row.id);
                            return [...next, p.row];
                        });
                    }
                },
            });
            setResults(outcome.cases || []);
            setSummary(outcome.summary || null);
            if (outcome.runs) setRuns(outcome.runs);
            else await refreshRuns();
            if (outcome.run_id) {
                setActiveRun({
                    id: outcome.run_id,
                    name: outcome.run?.name,
                    saved_at: outcome.summary?.finished_at,
                    suite,
                    summary: outcome.summary,
                    cases: outcome.cases,
                });
                setView('detail');
            }
        } catch (err) {
            if (err?.name !== 'AbortError') {
                setError(err.message || String(err));
            }
        } finally {
            setRunning(false);
            setProgress(null);
            abortRef.current = null;
        }
    }, [instituteId, suite, allowWrites, runName, running, refreshRuns]);

    if (!open) return null;

    return (
        <div className="tbx-eval-overlay" role="dialog" aria-label="Tabexawy health checks">
            <div className="tbx-eval-panel" dir={isRtl ? 'rtl' : 'ltr'}>
                <div className="tbx-eval-head">
                    <div>
                        <div className="tbx-eval-title">
                            {isRtl ? 'فحوصات تابكسوي' : 'Tabexawy health checks'}
                        </div>
                        <div className="tbx-eval-sub">
                            {isRtl
                                ? 'كل تشغيل يتحفظ كمجموعة — تقدر تفتحها وتقارن لاحقاً'
                                : 'Each run is saved as a collection — open and compare later'}
                        </div>
                    </div>
                    <button type="button" className="tbx-eval-close" onClick={onClose} aria-label="Close">
                        ✕
                    </button>
                </div>

                <div className="tbx-eval-tabs">
                    <button
                        type="button"
                        className={view === 'history' || view === 'compare' ? 'is-active' : ''}
                        onClick={() => setView('history')}
                        disabled={running}
                    >
                        {isRtl ? 'المجموعات' : 'Collections'}
                    </button>
                    <button
                        type="button"
                        className={view === 'draft' || (view === 'detail' && running) ? 'is-active' : ''}
                        onClick={startNewTest}
                        disabled={running}
                    >
                        {isRtl ? 'اختبار جديد' : 'New test'}
                    </button>
                    {activeRun && !running && (
                        <button
                            type="button"
                            className={view === 'detail' ? 'is-active' : ''}
                            onClick={() => setView('detail')}
                        >
                            {isRtl ? 'التفاصيل' : 'Details'}
                        </button>
                    )}
                </div>

                {error && <div className="tbx-eval-error">{error}</div>}

                {view === 'history' && (
                    <div className="tbx-eval-history">
                        <div className="tbx-eval-history-actions">
                            <button type="button" className="tbx-eval-run" onClick={startNewTest}>
                                {isRtl ? 'تشغيل جديد' : 'Start new run'}
                            </button>
                            <button
                                type="button"
                                className="tbx-eval-secondary"
                                disabled={compareIds.length !== 2}
                                onClick={openCompare}
                            >
                                {isRtl
                                    ? `مقارنة (${compareIds.length}/2)`
                                    : `Compare (${compareIds.length}/2)`}
                            </button>
                        </div>
                        {!runs.length && (
                            <div className="tbx-eval-empty">
                                {isRtl
                                    ? 'لا توجد مجموعات محفوظة بعد. ابدأ اختباراً جديداً.'
                                    : 'No saved collections yet. Start a new test.'}
                            </div>
                        )}
                        {runs.map((run) => {
                            const o = formatOverall(run.summary);
                            const selected = compareIds.includes(run.id);
                            return (
                                <div key={run.id} className={`tbx-eval-collection ${selected ? 'is-selected' : ''}`}>
                                    <button
                                        type="button"
                                        className="tbx-eval-collection-main"
                                        onClick={() => openRun(run.id)}
                                    >
                                        <div className="tbx-eval-collection-title">{run.name}</div>
                                        <div className="tbx-eval-collection-meta">
                                            {run.suite} · {run.saved_at ? new Date(run.saved_at).toLocaleString() : '—'}
                                        </div>
                                        <div className="tbx-eval-collection-stats">
                                            {o.passed}/{o.total} · {Number(o.tokens).toLocaleString()} tok · {o.cost} ·{' '}
                                            {o.seconds}s
                                        </div>
                                    </button>
                                    <div className="tbx-eval-collection-side">
                                        <label className="tbx-eval-check compact">
                                            <input
                                                type="checkbox"
                                                checked={selected}
                                                onChange={() => toggleCompare(run.id)}
                                            />
                                            <span>{isRtl ? 'قارن' : 'Compare'}</span>
                                        </label>
                                        <button type="button" onClick={() => renameRun(run.id, run.name)}>
                                            {isRtl ? 'إعادة تسمية' : 'Rename'}
                                        </button>
                                        <button type="button" onClick={() => removeRun(run.id)}>
                                            {isRtl ? 'حذف' : 'Delete'}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {view === 'draft' && (
                    <>
                        <div className="tbx-eval-controls">
                            <label className="tbx-eval-field">
                                <span>{isRtl ? 'اسم المجموعة (اختياري)' : 'Collection name (optional)'}</span>
                                <input
                                    type="text"
                                    value={runName}
                                    disabled={running}
                                    placeholder={
                                        isRtl ? 'مثال: قبل إصلاح التقارير' : 'e.g. Before reports fix'
                                    }
                                    onChange={(e) => setRunName(e.target.value)}
                                />
                            </label>
                            <label className="tbx-eval-field">
                                <span>{isRtl ? 'المجموعة' : 'Suite'}</span>
                                <select
                                    value={suite}
                                    disabled={running}
                                    onChange={(e) => setSuite(e.target.value)}
                                >
                                    <option value="ask">{isRtl ? 'أسئلة (آمن)' : 'Ask (safe)'}</option>
                                    <option value="actions">{isRtl ? 'إجراءات' : 'Actions'}</option>
                                    <option value="all">{isRtl ? 'الكل' : 'All'}</option>
                                </select>
                            </label>

                            {(suite === 'actions' || suite === 'all') && (
                                <label className="tbx-eval-check">
                                    <input
                                        type="checkbox"
                                        checked={allowWrites}
                                        disabled={running}
                                        onChange={(e) => setAllowWrites(e.target.checked)}
                                    />
                                    <span>
                                        {isRtl
                                            ? 'السماح بكتابة بيانات حقيقية'
                                            : 'Allow real write actions'}
                                    </span>
                                </label>
                            )}

                            <div className="tbx-eval-actions">
                                {!running ? (
                                    <button type="button" className="tbx-eval-run" onClick={run}>
                                        {isRtl ? 'تشغيل وحفظ' : 'Run & save'}
                                    </button>
                                ) : (
                                    <button type="button" className="tbx-eval-stop" onClick={stop}>
                                        {isRtl ? 'إيقاف' : 'Stop'}
                                    </button>
                                )}
                            </div>
                        </div>

                        {progress && (
                            <div className="tbx-eval-progress">
                                {progress.status === 'starting'
                                    ? isRtl
                                        ? 'جاري التحميل…'
                                        : 'Starting…'
                                    : `${progress.index + 1}/${progress.total} — ${
                                          progress.title || progress.caseId
                                      }${progress.detail ? ` · ${progress.detail}` : ''}`}
                            </div>
                        )}

                        {!!results.length && (
                            <div className="tbx-eval-summary-wrap">
                                <OverallCards
                                    summary={
                                        summary || {
                                            passed: results.filter((r) => r.passed).length,
                                            failed: results.filter((r) => !r.passed).length,
                                            total: results.length,
                                            total_tokens: results.reduce(
                                                (s, r) => s + (Number(r.total_tokens) || 0),
                                                0,
                                            ),
                                            cost_usd: results.reduce(
                                                (s, r) => s + (Number(r.cost_usd) || 0),
                                                0,
                                            ),
                                            elapsed_seconds: Number(
                                                (
                                                    results.reduce((s, r) => s + (Number(r.ms) || 0), 0) /
                                                    1000
                                                ).toFixed(1),
                                            ),
                                        }
                                    }
                                    isRtl={isRtl}
                                />
                            </div>
                        )}

                        <div className="tbx-eval-list">
                            {results.map((row) => (
                                <EvalCaseRow key={row.id} row={row} isRtl={isRtl} />
                            ))}
                        </div>
                    </>
                )}

                {view === 'detail' && activeRun && (
                    <>
                        <div className="tbx-eval-detail-head">
                            <div>
                                <div className="tbx-eval-collection-title">{activeRun.name}</div>
                                <div className="tbx-eval-collection-meta">
                                    {activeRun.suite} ·{' '}
                                    {activeRun.saved_at
                                        ? new Date(activeRun.saved_at).toLocaleString()
                                        : '—'}
                                </div>
                            </div>
                            <div className="tbx-eval-actions">
                                <button
                                    type="button"
                                    className="tbx-eval-secondary"
                                    onClick={() => renameRun(activeRun.id, activeRun.name)}
                                >
                                    {isRtl ? 'إعادة تسمية' : 'Rename'}
                                </button>
                                <button type="button" className="tbx-eval-run" onClick={startNewTest}>
                                    {isRtl ? 'اختبار جديد' : 'New test'}
                                </button>
                            </div>
                        </div>
                        <div className="tbx-eval-summary-wrap">
                            <OverallCards summary={activeRun.summary || summary} isRtl={isRtl} />
                        </div>
                        <div className="tbx-eval-list">
                            {(activeRun.cases || results).map((row) => (
                                <EvalCaseRow key={row.id} row={row} isRtl={isRtl} />
                            ))}
                        </div>
                    </>
                )}

                {view === 'compare' && compareRuns.left && compareRuns.right && (
                    <div className="tbx-eval-list">
                        <div className="tbx-eval-actions" style={{ padding: '8px 4px' }}>
                            <button
                                type="button"
                                className="tbx-eval-secondary"
                                onClick={() => setView('history')}
                            >
                                {isRtl ? 'رجوع للمجموعات' : 'Back to collections'}
                            </button>
                        </div>
                        <CompareView
                            left={compareRuns.left}
                            right={compareRuns.right}
                            isRtl={isRtl}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
